import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Trash2, RotateCcw, Download, Upload, Loader2 } from 'lucide-react';
import { DatasetHub } from './screens/DatasetHub';
import { OverviewScreen } from './screens/OverviewScreen';
import { InspectScreen } from './screens/InspectScreen';
import { AppShell } from './layout/AppShell';
import type { StatusBarItem } from './layout/StatusBar';
import { apiService, type UpdateDocumentPayload } from './services/api';
import { UploadModal } from './components/UploadModal';
import { ReprocessModal, type ReprocessOperation } from './components/ReprocessModal';
import { DuplicatesModal, type DuplicatePairItem } from './components/DuplicatesModal';
import { FilevinePullModal, type FilevinePullSubmission } from './components/FilevinePullModal';
import { FilevinePushModal } from './components/FilevinePushModal';
import { LinkFilevineProjectModal } from './components/LinkFilevineProjectModal';
import { MergeDocumentsModal } from './components/MergeDocumentsModal';
import { JobsIndicator } from './components/JobsIndicator';
import { WelcomeModal } from './components/WelcomeModal';
import {
  ModalOverlay,
  ModalPanel,
  ModalTitle,
  ModalActions,
  ModalButton,
  ModalStatusMessage,
} from './components/shared/Modal';
import { useAuth } from './auth/AuthProvider';
import type { PaginationModel } from './types/table';
import {
  DatasetListItem,
  DatasetSummary,
  DocumentOverviewItem,
  TaxonomyTreeNode,
  DocumentOverviewQueryResult,
  DocumentSummaryStats,
  DocumentDetailData,
  DocumentReviewStatus,
  DocumentStatusOption,
  DocumentTag,
  ActiveJob,
  TrackedJob,
  DocumentDate,
} from './types';
import type { DateRangeValue } from './components/DocumentTable';
import type { SummaryAction } from './components/DocumentSummaryHeader';
import type { DocumentInspectHeaderProps, InspectHeaderAction } from './components/documentInspect/DocumentInspectHeader';
import type { DocumentPageRailProps } from './components/documentInspect/DocumentPageRail';
import type { CanvasPage } from './components/documentInspect/DocumentCanvasPanel';
import type {
  DocumentInspectWorkspaceProps,
  DocumentPageActions,
} from './components/documentInspect/DocumentInspectWorkspace';
import { useJobs, trackedJobToActiveJob } from './context/JobContext';
import { useDebouncedValue } from './hooks/useDebouncedValue';
import { formatLocalDateTime } from './utils/format';

type ViewState = 'datasets' | 'overview' | 'inspect';

type ReprocessModalState =
  | { scope: 'dataset'; metadataPath: string; title: string }
  | { scope: 'document'; metadataPath: string; documentId: string; title: string };

const debugSelection = (...args: unknown[]) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('[DocumentInspect]', ...args);
  }
};

const isDebugMode = () => {
  return process.env.REACT_APP_DEBUG_MODE === 'true';
};

const EMPTY_SUMMARY: DocumentSummaryStats = {
  totalDocuments: 0,
  totalPages: 0,
  duplicatePairs: 0,
  lowConfidenceDocuments: 0,
  reviewCompletion: 0,
  orphanPages: 0,
};

const DEFAULT_DOCUMENT_PAGE_SIZE = (() => {
  try {
    const stored = localStorage.getItem('bigpdf:pageSize');
    if (stored) { const n = Number(stored); if ([10, 25, 50, 100].includes(n)) return n; }
  } catch {}
  return 25;
})();

const placeholderImage = (label: string) =>
  `https://placehold.co/600x800/f8fafc/e2e8f0?text=${encodeURIComponent(label)}`;

const PRESENCE_STORAGE_KEY = 'bigpdf:presenceEditorId';

const getCaseDisplayTitle = (dataset: DatasetListItem | null): string | undefined => {
  if (!dataset) {
    return undefined;
  }
  const normalized = dataset.metadataPath.replace(/\\+/g, '/');
  const segments = normalized.split('/').filter(Boolean);
  let name = segments.length > 1 ? segments[segments.length - 2] : null;
  if (name) {
    name = name.replace(/_dataset$/i, '');
  }
  return name || dataset.title || undefined;
};

const generateEditorId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `reviewer-${crypto.randomUUID().slice(0, 8)}`;
  }
  return `reviewer-${Math.random().toString(36).slice(2, 10)}`;
};

const LoadingScreen = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 18px;
  font-weight: 500;
`;

export const App: React.FC = () => {
  const { isLoading, isAuthenticated, error: authError } = useAuth();
  const debugMode = useMemo(() => isDebugMode(), []);
  const [view, setView] = useState<ViewState>(() => {
    const hash = window.location.hash;
    if (hash.includes('/inspect/')) return 'inspect';
    if (hash.includes('/overview')) return 'overview';
    return 'datasets';
  });
  const [datasets, setDatasets] = useState<DatasetListItem[]>([]);
  const [datasetsVersion, setDatasetsVersion] = useState(0);
  // Bumped when a job completes so the open case's document overview re-fetches
  // (picks up reprocess results like refreshed duplicate badges / titles / dates).
  const [documentsVersion, setDocumentsVersion] = useState(0);
  const [selectedDataset, setSelectedDataset] = useState<string>(() => {
    const hash = window.location.hash;
    const match = hash.match(/dataset=([^&]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  });
  const [summary, setSummary] = useState<DatasetSummary | null>(null);
  const [documents, setDocuments] = useState<DocumentOverviewItem[]>([]);
  const documentsRef = useRef<DocumentOverviewItem[]>([]);
  useEffect(() => {
    documentsRef.current = documents;
  }, [documents]);
  const [documentsTotal, setDocumentsTotal] = useState(0);
  const [tagPool, setTagPool] = useState<string[]>([]);
  const [documentThumbnails, setDocumentThumbnails] = useState<Record<string, (string | null)[]>>({});
  const [searchQuery, setSearchQuery] = useState<string>('');
  // Debounced view of the search input — gates backend fetches so the case
  // overview and per-document detail don't refetch on every keystroke.
  // The UI itself still reads `searchQuery` directly so the input stays
  // snappy and any already-loaded text highlights immediately.
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 250);
  const [duplicateFilterDocId, setDuplicateFilterDocId] = useState<string | null>(null);
  const [activeStatus, setActiveStatus] = useState<string | null>(null);
  // category filter value: a bare section (matches all leaves) or `section/leaf`.
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [categoryTree, setCategoryTree] = useState<TaxonomyTreeNode[]>([]);
  const [dateRange, setDateRange] = useState<DateRangeValue | null>(null);
  const [sortOption, setSortOption] = useState<string>('nominal');
  const [documentsPaginationModel, setDocumentsPaginationModel] = useState<PaginationModel>({
    page: 0,
    pageSize: DEFAULT_DOCUMENT_PAGE_SIZE,
  });
  const [datasetsError, setDatasetsError] = useState<string | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [documentsError, setDocumentsError] = useState<string | null>(null);
  const [isLoadingDatasets, setIsLoadingDatasets] = useState<boolean>(false);
  const [isLoadingSummary, setIsLoadingSummary] = useState<boolean>(false);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState<boolean>(false);
  const [documentWarnings, setDocumentWarnings] = useState<string[]>([]);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(() => {
    const hash = window.location.hash;
    const match = hash.match(/\/inspect\/([^?]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  });
  const [documentDetail, setDocumentDetail] = useState<DocumentDetailData | null>(null);
  const [documentDuplicates, setDocumentDuplicates] = useState<DuplicatePairItem[] | null>(null);
  const [detailWarnings, setDetailWarnings] = useState<string[]>([]);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);
  const [detailVersion, setDetailVersion] = useState<number>(0);
  const [isUpdatingDetail, setIsUpdatingDetail] = useState<boolean>(false);
  const [detailActionMessage, setDetailActionMessage] = useState<string | null>(null);
  const [detailActionError, setDetailActionError] = useState<string | null>(null);
  const [selectedPageIds, setSelectedPageIds] = useState<string[]>([]);
  const [isUploadModalVisible, setUploadModalVisible] = useState<boolean>(false);
  const [casePendingDelete, setCasePendingDelete] = useState<DatasetListItem | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeletingCase, setIsDeletingCase] = useState<boolean>(false);
  const { jobs: activeJobs, trackedJobs, loading: jobsLoading, error: jobsError, upsertJob: upsertJobState, removeJob: removeJobFromState, refreshJobs: refreshTrackedJobs, pollJob } = useJobs();
  const [reprocessModalState, setReprocessModalState] = useState<ReprocessModalState | null>(null);
  const [duplicatesModalState, setDuplicatesModalState] = useState<{ datasetPath: string; documentId?: string | null } | null>(null);
  const [filevinePullState, setFilevinePullState] = useState<{ metadataPath: string } | null>(null);
  const [filevinePushState, setFilevinePushState] = useState<{ metadataPath: string } | null>(null);
  const [linkFilevineOpen, setLinkFilevineOpen] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState(false);
  // Multi-doc selection in the overview table. Persists across pagination,
  // sort, and filter changes for the current case; cleared when the user
  // changes cases (see effect below) or after a successful structural
  // action that invalidates the selection (e.g. merge).
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);
  const [selectionAnchorDocId, setSelectionAnchorDocId] = useState<string | null>(null);
  // Cache of full doc objects for selected ids — selection survives
  // pagination, so when a selected doc scrolls off the loaded page we still
  // need its title/pageCount for the merge confirmation modal.
  const [selectedDocCache, setSelectedDocCache] = useState<Record<string, DocumentOverviewItem>>({});
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergeSurvivorDocId, setMergeSurvivorDocId] = useState<string | null>(null);

  // Reset selection on case change — selection is meaningless across
  // cases and persisting it would let a stale doc-id leak into a merge.
  useEffect(() => {
    setSelectedDocIds([]);
    setSelectionAnchorDocId(null);
    setSelectedDocCache({});
  }, [selectedDataset]);

  // Keep the selected-doc cache in sync with the loaded page: refresh
  // entries that appear in `documents` (so titles/pageCounts stay current
  // after edits), and drop entries for docs that are no longer selected.
  useEffect(() => {
    setSelectedDocCache(prev => {
      const selectedSet = new Set(selectedDocIds);
      const next: Record<string, DocumentOverviewItem> = {};
      for (const id of selectedDocIds) {
        const fromPage = documents.find(d => d.docId === id);
        const cached = prev[id];
        if (fromPage) {
          next[id] = fromPage;
        } else if (cached) {
          next[id] = cached;
        }
        // If neither — the doc was selected on a prior page and has since
        // been removed (e.g. by a merge that absorbed it). It will be
        // dropped from the cache; the next selection-state recompute will
        // handle pruning the id from selectedDocIds when needed.
      }
      // Prune any cache entries no longer selected (defensive — could
      // happen if external code edits selection without going through the
      // toggles, though nothing currently does).
      for (const id of Object.keys(prev)) {
        if (!selectedSet.has(id)) delete next[id];
      }
      return next;
    });
  }, [documents, selectedDocIds]);

  const handleToggleSelection = useCallback(
    (docId: string, opts: { range?: boolean; visibleDocIds: string[] }) => {
      if (opts.range && selectionAnchorDocId) {
        // Shift-click: select the range from the anchor to this doc within the
        // currently-visible page. The anchor stays put so subsequent
        // shift-clicks extend from the same starting point (matches every
        // other multi-select UI the user has touched).
        const anchorIdx = opts.visibleDocIds.indexOf(selectionAnchorDocId);
        const targetIdx = opts.visibleDocIds.indexOf(docId);
        if (anchorIdx >= 0 && targetIdx >= 0) {
          const [lo, hi] = anchorIdx < targetIdx ? [anchorIdx, targetIdx] : [targetIdx, anchorIdx];
          const rangeIds = opts.visibleDocIds.slice(lo, hi + 1);
          setSelectedDocIds(prev => {
            const set = new Set(prev);
            for (const id of rangeIds) set.add(id);
            return Array.from(set);
          });
          return;
        }
      }
      // Plain click toggles a single row and resets the anchor to here.
      setSelectionAnchorDocId(docId);
      setSelectedDocIds(prev => {
        const set = new Set(prev);
        if (set.has(docId)) set.delete(docId);
        else set.add(docId);
        return Array.from(set);
      });
    },
    [selectionAnchorDocId],
  );

  const handleToggleSelectAllOnPage = useCallback(
    (visibleDocIds: string[], select: boolean) => {
      setSelectedDocIds(prev => {
        const set = new Set(prev);
        if (select) {
          for (const id of visibleDocIds) set.add(id);
        } else {
          for (const id of visibleDocIds) set.delete(id);
        }
        return Array.from(set);
      });
      setSelectionAnchorDocId(null);
    },
    [],
  );

  const handleClearSelection = useCallback(() => {
    setSelectedDocIds([]);
    setSelectionAnchorDocId(null);
  }, []);

  // Compute the full list of selected doc objects, in case order. Drives
  // the merge modal's display and the survivor default. Only docs we have
  // in the cache are surfaced — a selected id with no cached object is
  // treated as effectively deselected for merge purposes.
  const selectedDocs = useMemo(() => {
    const list: DocumentOverviewItem[] = [];
    for (const id of selectedDocIds) {
      const doc = selectedDocCache[id];
      if (doc) list.push(doc);
    }
    return list.sort((a, b) => (a.firstPageIndex ?? 0) - (b.firstPageIndex ?? 0));
  }, [selectedDocIds, selectedDocCache]);

  const handleOpenMergeModal = useCallback(() => {
    if (selectedDocs.length < 2) return;
    // Default survivor: the earliest doc by case order. Matches the
    // existing rail-merge behavior (survivor = the doc the user is
    // "in"; in the rail the open doc is always the survivor) and tends to
    // be the doc with the most context (cover page / title).
    setMergeSurvivorDocId(selectedDocs[0].docId);
    setMergeModalOpen(true);
  }, [selectedDocs]);

  const thumbnailRequests = useRef<Set<string>>(new Set());
  const thumbnailCacheRef = useRef<Record<string, (string | null)[]>>({});
  const lastSelectedPageIdRef = useRef<string | null>(null);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const hash = window.location.hash;
      if (hash.includes('/inspect/')) {
        const match = hash.match(/\/inspect\/([^?]+)/);
        const docId = match ? decodeURIComponent(match[1]) : null;
        const datasetMatch = hash.match(/dataset=([^&]+)/);
        const dataset = datasetMatch ? decodeURIComponent(datasetMatch[1]) : '';

        setView('inspect');
        setSelectedDocumentId(docId);
        if (dataset) setSelectedDataset(dataset);
      } else if (hash.includes('/overview')) {
        const datasetMatch = hash.match(/dataset=([^&]+)/);
        const dataset = datasetMatch ? decodeURIComponent(datasetMatch[1]) : '';

        setView('overview');
        setSelectedDocumentId(null);
        if (dataset) setSelectedDataset(dataset);
      } else {
        setView('datasets');
        setSelectedDocumentId(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Update URL when view changes
  useEffect(() => {
    let hash = '';
    if (view === 'inspect' && selectedDocumentId && selectedDataset) {
      hash = `#/inspect/${encodeURIComponent(selectedDocumentId)}?dataset=${encodeURIComponent(selectedDataset)}`;
    } else if (view === 'overview' && selectedDataset) {
      hash = `#/overview?dataset=${encodeURIComponent(selectedDataset)}`;
    } else {
      hash = '#/datasets';
    }

    if (window.location.hash !== hash) {
      window.history.pushState(null, '', hash);
    }
  }, [view, selectedDataset, selectedDocumentId]);

  const [presenceEditorId] = useState<string>(() => {
    if (typeof window === 'undefined') {
      return generateEditorId();
    }
    try {
      const existing = window.localStorage.getItem(PRESENCE_STORAGE_KEY);
      if (existing) {
        return existing;
      }
      const generated = generateEditorId();
      window.localStorage.setItem(PRESENCE_STORAGE_KEY, generated);
      return generated;
    } catch (error) {
      console.warn('Failed to access localStorage for presence editor id', error);
      return generateEditorId();
    }
  });

  const mergeActiveEditors = useCallback(
    (list: string[] | undefined): string[] => {
      const seen = new Set<string>();
      const result: string[] = [];
      (list ?? []).forEach(editor => {
        if (!editor || seen.has(editor)) {
          return;
        }
        seen.add(editor);
        result.push(editor);
      });
      if (presenceEditorId && !seen.has(presenceEditorId)) {
        result.push(presenceEditorId);
      }
      return result;
    },
    [presenceEditorId],
  );

  const mergeOverviewResult = useCallback((result: DocumentOverviewQueryResult) => {
    thumbnailRequests.current.clear();
    const previousDocs = documentsRef.current;
    const previousRefsByDoc = new Map<string, string>();
    previousDocs.forEach(doc => {
      previousRefsByDoc.set(doc.docId, (doc.thumbnailRefs ?? []).join(','));
    });
    setDocumentThumbnails(prev => {
      const next: Record<string, (string | null)[]> = {};
      result.documents.forEach(doc => {
        if (!prev[doc.docId]) return;
        const previousKey = previousRefsByDoc.get(doc.docId);
        const currentKey = (doc.thumbnailRefs ?? []).join(',');
        // Drop cached thumbnails if the underlying page refs changed
        // (e.g., a page was removed/moved/merged into/out of this doc).
        if (previousKey !== undefined && previousKey !== currentKey) return;
        next[doc.docId] = prev[doc.docId];
      });
      return next;
    });

    setDocuments(result.documents);
    setDocumentsTotal(result.total);
    setDocumentWarnings(result.warnings ?? []);
    setDocumentsError(null);
  }, []);

  // Auto-refresh datasets when a job completes
  const completedJobIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const newlyCompleted: string[] = [];
    for (const [jobId, job] of Object.entries(trackedJobs)) {
      if (job.status === 'DONE' && !completedJobIdsRef.current.has(jobId)) {
        newlyCompleted.push(jobId);
      }
    }
    if (newlyCompleted.length > 0) {
      for (const id of newlyCompleted) {
        completedJobIdsRef.current.add(id);
      }
      setDatasetsVersion(prev => prev + 1);
      // Re-fetch the open case's documents AND the open document detail too — a
      // completed reprocess (e.g. duplicate detection) rewrites case artifacts
      // that drive the overview badges and the inspector's duplicate pane; without
      // this the open views show stale results ("completes with no effect").
      setDocumentsVersion(prev => prev + 1);
      setDetailVersion(prev => prev + 1);
    }
    // Also track other terminal states to avoid re-triggering
    for (const [jobId, job] of Object.entries(trackedJobs)) {
      if (['DONE', 'FAILED', 'CANCELLED'].includes(job.status ?? '')) {
        completedJobIdsRef.current.add(jobId);
      }
    }
  }, [trackedJobs]);

  // Single source of truth: tracker jobs, converted to ActiveJob for display.
  // Filter out export jobs (handled by export/download buttons).
  const combinedJobs = useMemo(() => {
    return Object.values(trackedJobs)
      .map(tj => trackedJobToActiveJob(tj))
      .filter(job => job.jobType !== 'export')
      .sort((a, b) => {
        const aTime = a.updatedAt ?? a.createdAt ?? 0;
        const bTime = b.updatedAt ?? b.createdAt ?? 0;
        return bTime - aTime;
      });
  }, [trackedJobs]);

  const fetchDocuments = useCallback(() => {
    if (!selectedDataset) {
      return undefined;
    }
    const { page, pageSize } = documentsPaginationModel;
    const offset = page * pageSize;
    const requestOptions = {
      search: debouncedSearchQuery || undefined,
      status: activeStatus || undefined,
      category: activeCategory || undefined,
      dateRange: dateRange || undefined,
      sort: sortOption,
      limit: pageSize,
      offset,
      sharesDuplicatesWith: duplicateFilterDocId || undefined,
    };

    const controller = { cancelled: false };
    setIsLoadingDocuments(true);

    apiService
      .fetchDatasetDocuments(selectedDataset, requestOptions)
      .then((result: DocumentOverviewQueryResult) => {
        if (controller.cancelled) {
          return;
        }
        mergeOverviewResult(result);
      })
      .catch(() => {
        if (controller.cancelled) {
          return;
        }
        setDocumentsError('Failed to load document overview');
        setDocuments([]);
        setDocumentsTotal(0);
        setDocumentWarnings([]);
      })
      .finally(() => {
        if (controller.cancelled) {
          return;
        }
        setIsLoadingDocuments(false);
      });

    return () => {
      controller.cancelled = true;
    };
  }, [selectedDataset, documentsPaginationModel, debouncedSearchQuery, activeStatus, activeCategory, sortOption, dateRange, duplicateFilterDocId, mergeOverviewResult]);

  useEffect(() => {
    thumbnailCacheRef.current = documentThumbnails;
  }, [documentThumbnails]);

  // Load the checklist taxonomy once for the category filter dropdown. The tree
  // is global (not per-dataset), so fetch it on mount; failure just hides the
  // Category chip (DocumentTable only renders it when the tree is non-empty).
  useEffect(() => {
    let cancelled = false;
    apiService
      .fetchTaxonomySections()
      .then(tree => { if (!cancelled) setCategoryTree(tree); })
      .catch(() => { /* leave the filter hidden if the taxonomy can't load */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Don't fetch datasets until authentication is complete
    if (isLoading || !isAuthenticated) {
      return;
    }

    let cancelled = false;
    setIsLoadingDatasets(true);
    apiService
      .fetchDatasets()
      .then(items => {
        if (cancelled) {
          return;
        }
        setDatasets(items);
        setDatasetsError(null);
        setSelectedDataset(prev => {
          if (!prev) {
            return '';
          }
          const stillExists = items.some(item => item.metadataPath === prev);
          return stillExists ? prev : '';
        });
        if (!items.length) {
          setView('datasets');
        }
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setDatasetsError('Failed to load cases');
        setDatasets([]);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingDatasets(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [datasetsVersion, isLoading, isAuthenticated]);

  // Always refetch the case list when the hub becomes visible. The on-DONE
  // bump below refreshes the list live while you're watching, but a job that
  // completes (e.g. a Filevine pull creating a new case) while you're on
  // another view marks itself "seen" and won't re-trigger — so landing on the
  // hub must reload independently, or a freshly-created case is missing until
  // a manual refresh.
  useEffect(() => {
    if (view === 'datasets' && isAuthenticated && !isLoading) {
      setDatasetsVersion(prev => prev + 1);
    }
    // Intentionally keyed on `view` (not datasetsVersion) so this fires on each
    // navigation to the hub without looping.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, isAuthenticated, isLoading]);

  useEffect(() => {
    if (!selectedDataset) {
      setSummary(null);
      setDocuments([]);
      setDocumentsTotal(0);
      setDocumentThumbnails({});
      thumbnailRequests.current.clear();
      setDocumentWarnings([]);
      setSelectedDocumentId(null);
      setTagPool([]);
      setView('datasets');
      return;
    }

    let cancelled = false;
    setIsLoadingSummary(true);
    apiService
      .fetchDatasetSummary(selectedDataset)
      .then(result => {
        if (cancelled) {
          return;
        }
        setSummary(result);
        setSummaryError(null);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setSummaryError('Failed to load case summary');
        setSummary(null);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingSummary(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedDataset]);

  useEffect(() => {
    if (!selectedDataset) {
      return;
    }
    let cancelled = false;
    apiService
      .fetchDatasetTags(selectedDataset)
      .then(tags => {
        if (!cancelled) {
          setTagPool(tags);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTagPool([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedDataset]);


  useEffect(() => {
    if (!selectedDataset) {
      setDocuments([]);
      setDocumentsTotal(0);
      setDocumentWarnings([]);
      setSelectedPageIds([]);
      setDocumentsPaginationModel(prev => prev.page === 0 ? prev : { ...prev, page: 0 });
      lastSelectedPageIdRef.current = null;
      return;
    }

    const cancel = fetchDocuments();
    return () => {
      cancel?.();
    };
  }, [selectedDataset, debouncedSearchQuery, activeStatus, sortOption, dateRange, documentsVersion, fetchDocuments]);

  useEffect(() => {
    if (!selectedDataset || !selectedDocumentId) {
      setDocumentDetail(null);
      setDocumentDuplicates(null);
      setDetailWarnings([]);
      setDetailError(null);
      setIsLoadingDetail(false);
      setDetailActionMessage(null);
      setDetailActionError(null);
      setIsUpdatingDetail(false);
      setSelectedPageIds([]);
      lastSelectedPageIdRef.current = null;
      return;
    }

    let cancelled = false;
    setIsLoadingDetail(true);
    setDetailError(null);

    apiService
      .fetchDocumentDetail(selectedDataset, selectedDocumentId, { search: debouncedSearchQuery || undefined })
      .then(detail => {
        if (cancelled) {
          return;
        }
        console.log('[DocumentDetail] Raw response:', detail);
        const hydratedDetail: DocumentDetailData = {
          ...detail,
          header: {
            ...detail.header,
            activeEditors: mergeActiveEditors(detail.header.activeEditors),
          },
        };
        console.log('[DocumentDetail] Hydrated detail duplicatePairs:', hydratedDetail.duplicatePairs);
        // Open documents with nothing selected. The canvas viewer's own
        // fallback shows the first page; selection is purely opt-in via
        // checkboxes or the pill editor.
        const pageRailWithSelection = hydratedDetail.pageRail.map(page => ({
          ...page,
          selected: false,
        }));
        setDocumentDetail({
          ...hydratedDetail,
          pageRail: pageRailWithSelection,
        });
        setDetailWarnings(hydratedDetail.warnings ?? []);
        setDetailError(null);
        setSelectedPageIds([]);
        lastSelectedPageIdRef.current = null;

        // Use duplicate pairs from the document detail response
        // Check both camelCase and snake_case field names
        const duplicatePairs = hydratedDetail.duplicatePairs || hydratedDetail.duplicate_pairs || [];
        console.log('[Duplicates] Loaded', duplicatePairs.length, 'duplicate pairs for current document from detail response');

        // Debug: log which pages have duplicates
        const currentDocPageIds = new Set(hydratedDetail.pageRail.map(p => p.id));
        const pagesWithDuplicates = new Set<string>();
        duplicatePairs.forEach(pair => {
          if (currentDocPageIds.has(pair.left.page_id)) pagesWithDuplicates.add(pair.left.page_id);
          if (currentDocPageIds.has(pair.right.page_id)) pagesWithDuplicates.add(pair.right.page_id);
        });
        console.log('[Duplicates] Pages with duplicates:', Array.from(pagesWithDuplicates).sort());

        setDocumentDuplicates(duplicatePairs);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setDocumentDetail(null);
        setDocumentDuplicates(null);
        setDetailWarnings([]);
        setDetailError('Failed to load document detail');
        setSelectedPageIds([]);
        lastSelectedPageIdRef.current = null;
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingDetail(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [mergeActiveEditors, selectedDataset, selectedDocumentId, debouncedSearchQuery, detailVersion]);

  useEffect(() => {
    if (view !== 'inspect' || !selectedDataset || !selectedDocumentId) {
      return undefined;
    }

    let cancelled = false;
    let timerId: number | undefined;

    const sendHeartbeat = async () => {
      if (cancelled) {
        return;
      }
      try {
        const presence = await apiService.updatePresence(
          selectedDataset,
          selectedDocumentId,
          presenceEditorId,
        );
        const activeEditors = mergeActiveEditors(presence.activeEditors);
        setDocumentDetail(prev => {
          if (!prev) {
            return prev;
          }
          const current = prev.header.activeEditors ?? [];
          const unchanged =
            current.length === activeEditors.length &&
            current.every((value, index) => value === activeEditors[index]);
          if (unchanged) {
            return prev;
          }
          return {
            ...prev,
            header: {
              ...prev.header,
              activeEditors,
            },
          };
        });
        if (presence.warnings && presence.warnings.length) {
          console.debug('[PresenceHeartbeat] warnings', presence.warnings);
        }
      } catch (error) {
        console.warn('Presence heartbeat failed', error);
      } finally {
        if (!cancelled) {
          timerId = window.setTimeout(sendHeartbeat, 30000);
        }
      }
    };

    sendHeartbeat();

    return () => {
      cancelled = true;
      if (timerId !== undefined) {
        window.clearTimeout(timerId);
      }
    };
  }, [mergeActiveEditors, presenceEditorId, selectedDataset, selectedDocumentId, view]);

  useEffect(() => {
    if (!detailActionMessage) {
      return undefined;
    }
    const timer = window.setTimeout(() => setDetailActionMessage(null), 4000);
    return () => window.clearTimeout(timer);
  }, [detailActionMessage]);

  const handleRefreshDatasets = useCallback(() => {
    setDatasetsVersion(prev => prev + 1);
    void refreshTrackedJobs();
  }, [refreshTrackedJobs]);

  const handleOpenUploadModal = useCallback(() => {
    setUploadModalVisible(true);
  }, []);

  const handleCloseUploadModal = useCallback(() => {
    setUploadModalVisible(false);
  }, []);

  const openDatasetReprocess = useCallback(() => {
    if (!selectedDataset) {
      return;
    }
    const label = summary?.title ?? selectedDataset;
    setReprocessModalState({ scope: 'dataset', metadataPath: selectedDataset, title: label });
  }, [selectedDataset, summary?.title]);

  const openDocumentReprocess = useCallback((docId: string, title: string) => {
    if (!selectedDataset) {
      return;
    }
    setReprocessModalState({ scope: 'document', metadataPath: selectedDataset, documentId: docId, title });
  }, [selectedDataset]);

  const handleReprocessCancel = useCallback(() => {
    setReprocessModalState(null);
  }, []);

  const handleCancelJob = useCallback(
    async (jobId: string) => {
      try {
        const status = await apiService.cancelJob(jobId);
        upsertJobState(status);
        await refreshTrackedJobs();
      } catch (error) {
        console.error('Failed to cancel job', error);
      }
    },
    [refreshTrackedJobs, upsertJobState],
  );

  const handleDismissJob = useCallback(
    async (jobId: string) => {
      try {
        const result = await apiService.deleteJobRecord(jobId);
        if (result.removed) {
          removeJobFromState(jobId);
        }
        await refreshTrackedJobs();
      } catch (error) {
        console.error('Failed to dismiss job', error);
      }
    },
    [refreshTrackedJobs, removeJobFromState],
  );

  const handleClearCompletedJobs = useCallback(async () => {
    const terminal = combinedJobs.filter(j => ['DONE', 'FAILED', 'CANCELLED'].includes(j.status));
    await Promise.all(terminal.map(j => apiService.deleteJobRecord(j.jobId).catch(() => {})));
    await refreshTrackedJobs();
  }, [combinedJobs, refreshTrackedJobs]);

  const jobsIndicatorElement = useMemo(() => (
    <JobsIndicator
      jobs={combinedJobs}
      onCancel={handleCancelJob}
      onDismiss={handleDismissJob}
      onClearCompleted={handleClearCompletedJobs}
    />
  ), [combinedJobs, handleCancelJob, handleDismissJob, handleClearCompletedJobs]);

  const handleCaseCreated = useCallback(() => {
    setUploadModalVisible(false);
    setDatasetsVersion(prev => prev + 1);
    void refreshTrackedJobs();
  }, [refreshTrackedJobs]);

  const handleDatasetOpen = useCallback(
    (dataset: DatasetListItem) => {
      setSelectedDataset(dataset.metadataPath);
      setActiveStatus(null);
      setActiveCategory(null);
      setSearchQuery('');
      setSortOption('nominal');
      setDocumentsPaginationModel({ page: 0, pageSize: DEFAULT_DOCUMENT_PAGE_SIZE });
      setSelectedDocumentId(null);
      setDocumentThumbnails({});
      thumbnailRequests.current.clear();
      setView('overview');
    },
    [],
  );

  const handleRequestDeleteCase = useCallback((dataset: DatasetListItem) => {
    setCasePendingDelete(dataset);
    setDeleteError(null);
  }, []);

  const handleCancelDeleteCase = useCallback(() => {
    if (isDeletingCase) {
      return;
    }
    setCasePendingDelete(null);
    setDeleteError(null);
  }, [isDeletingCase]);

  const handleConfirmDeleteCase = useCallback(async () => {
    if (!casePendingDelete) {
      return;
    }
    const { metadataPath } = casePendingDelete;
    setIsDeletingCase(true);
    setDeleteError(null);
    try {
      await apiService.deleteDataset(metadataPath);
      setIsDeletingCase(false);
      setCasePendingDelete(null);
      setDatasets(prev => prev.filter(item => item.metadataPath !== metadataPath));
      void refreshTrackedJobs();
      setDatasetsVersion(prev => prev + 1);
      if (selectedDataset === metadataPath) {
        setSelectedDataset('');
        setSummary(null);
        setDocuments([]);
        setDocumentsPaginationModel({ page: 0, pageSize: DEFAULT_DOCUMENT_PAGE_SIZE });
        setDocumentThumbnails({});
        thumbnailRequests.current.clear();
        setDocumentWarnings([]);
        setSelectedDocumentId(null);
        setView('datasets');
      }
    } catch (error) {
      console.error('Failed to delete case', error);
      setDeleteError('Failed to delete case. Please try again.');
      setIsDeletingCase(false);
    }
  }, [casePendingDelete, refreshTrackedJobs, selectedDataset]);

  const handleStatusFilterChange = useCallback((id: string | null) => {
    setActiveStatus(id);
    setDocumentsPaginationModel(prev => ({ ...prev, page: 0 }));
  }, []);

  const handleCategoryFilterChange = useCallback((value: string | null) => {
    setActiveCategory(value);
    setDocumentsPaginationModel(prev => ({ ...prev, page: 0 }));
  }, []);

  const handleSortChange = useCallback((value: string) => {
    setSortOption(value);
    setDocumentsPaginationModel(prev => ({ ...prev, page: 0 }));
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    setDocumentsPaginationModel(prev => ({ ...prev, page: 0 }));
  }, []);

  const handleDateRangeChange = useCallback((range: DateRangeValue | null) => {
    setDateRange(range);
    setDocumentsPaginationModel(prev => ({ ...prev, page: 0 }));
  }, []);

  const handleDocumentsPageChange = useCallback((model: PaginationModel) => {
    setDocumentsPaginationModel(prev => {
      if (model.pageSize !== prev.pageSize) {
        try { localStorage.setItem('bigpdf:pageSize', String(model.pageSize)); } catch {}
        return { ...model, page: 0 };
      }
      return model;
    });
  }, []);

  const handleDocumentOpen = useCallback((docId: string) => {
    setSelectedDocumentId(docId);
    setView('inspect');
  }, []);

  const handleRequestThumbnails = useCallback(
    async (doc: DocumentOverviewItem) => {
      if (!selectedDataset) {
        return;
      }
      if (!doc.thumbnailRefs || doc.thumbnailRefs.length === 0) {
        return;
      }
      if (thumbnailCacheRef.current[doc.docId]) {
        return;
      }
      if (thumbnailRequests.current.has(doc.docId)) {
        return;
      }
      thumbnailRequests.current.add(doc.docId);
      try {
        const thumbnails = await Promise.all(
          doc.thumbnailRefs.map(ref =>
            apiService
              .fetchPageThumbnail(selectedDataset, ref)
              .catch(() => null),
          ),
        );
        const fallback = doc.thumbnailUrls ? [...doc.thumbnailUrls] : [null, null, null];
        const padded = thumbnails.some(item => item !== null)
          ? [...thumbnails, null, null].slice(0, 3)
          : fallback;
        setDocumentThumbnails(prev => ({ ...prev, [doc.docId]: padded }));
      } finally {
        thumbnailRequests.current.delete(doc.docId);
      }
    },
    [selectedDataset],
  );

  useEffect(() => {
    if (!documents.length) {
      return;
    }
    documents.slice(0, Math.min(6, documents.length)).forEach(doc => {
      void handleRequestThumbnails(doc);
    });
  }, [documents, handleRequestThumbnails]);

  const refreshOverviewDocuments = useCallback(async () => {
    if (!selectedDataset) {
      return;
    }
    const { page, pageSize } = documentsPaginationModel;
    const requestOptions = {
      search: debouncedSearchQuery || undefined,
      status: activeStatus || undefined,
      category: activeCategory || undefined,
      dateRange: dateRange || undefined,
      sort: sortOption,
      limit: pageSize,
      offset: page * pageSize,
    };
    try {
      const result = await apiService.fetchDatasetDocuments(selectedDataset, requestOptions);
      mergeOverviewResult(result);
    } catch (error) {
      console.error('Failed to refresh document overview', error);
      setDocumentsError('Failed to refresh document overview');
    }
  }, [selectedDataset, debouncedSearchQuery, activeStatus, activeCategory, sortOption, dateRange, documentsPaginationModel, mergeOverviewResult]);

  const handleReprocessConfirm = useCallback(
    async (operations: ReprocessOperation[]) => {
      if (!reprocessModalState || operations.length === 0) {
        return;
      }

      const modalState = reprocessModalState;
      setReprocessModalState(null);

      const opList = operations.map(op => op.toLowerCase());
      try {
        if (modalState.scope === 'dataset') {
          await apiService.reprocessDataset(modalState.metadataPath, opList);
        } else {
          await apiService.reprocessDocument(modalState.metadataPath, modalState.documentId, opList);
        }
        // Job is now PENDING in the tracker. The 3-second refresh will pick it up.
        void refreshTrackedJobs();
      } catch (error) {
        console.error('Failed to start reprocess job', error);
        if (modalState.scope === 'document') {
          setDetailActionError('Failed to start reprocess job. Please try again.');
        } else {
          setDatasetsError('Failed to start case reprocess.');
        }
      }
    },
    [reprocessModalState, refreshTrackedJobs],
  );

  const handleFilevinePull = useCallback(
    async (submission: FilevinePullSubmission) => {
      if (!filevinePullState) return;
      // Throws on failure so the modal can surface the error and stay open.
      await apiService.pullFromFilevine(filevinePullState.metadataPath, submission);
      // One PENDING job per document (or per group) is now in the tracker; the
      // jobs poll picks them up and the version-bump-on-DONE effect refetches
      // this case as they finish.
      void refreshTrackedJobs();
    },
    [filevinePullState, refreshTrackedJobs],
  );

  const handleListFilevineDocuments = useCallback(
    async (params: { q?: string; offset?: number }) => {
      if (!filevinePullState) throw new Error('No case selected');
      const page = await apiService.listFilevineDocuments(filevinePullState.metadataPath, params);
      return {
        documents: page.documents.map(d => ({
          documentId: d.document_id,
          filename: d.filename,
          folderName: d.folder_name,
          size: d.size,
          uploadDate: d.upload_date,
          alreadyPulled: d.already_pulled,
        })),
        offset: page.offset,
        hasMore: page.has_more,
        count: page.count,
      };
    },
    [filevinePullState],
  );

  const handleListFilevineFolders = useCallback(
    async (parentFolderId?: number) => {
      if (!filevinePullState) throw new Error('No case selected');
      const level = await apiService.listFilevineFolders(filevinePullState.metadataPath, parentFolderId);
      return {
        parentFolderId: level.parent_folder_id,
        folders: level.folders.map(f => ({ folderId: f.folder_id, name: f.name })),
        documents: level.documents.map(d => ({
          documentId: d.document_id,
          filename: d.filename,
          folderName: d.folder_name,
          size: d.size,
          uploadDate: d.upload_date,
          alreadyPulled: d.already_pulled,
        })),
        documentsTruncated: level.documents_truncated,
      };
    },
    [filevinePullState],
  );

  const handleFilevinePush = useCallback(
    async ({ selectedOnly }: { selectedOnly: boolean }) => {
      if (!filevinePushState) return;
      // When "selected only" is chosen, scope the push to the UI-selected docs;
      // otherwise push the whole case (undefined = all).
      const documentIds = selectedOnly && selectedDocIds.length > 0
        ? selectedDocIds
        : undefined;
      // Throws on failure so the modal can surface the error and stay open.
      await apiService.pushToFilevine(filevinePushState.metadataPath, { documentIds });
      // FILEVINE_PUSH is now PENDING; the jobs poll surfaces its progress.
      void refreshTrackedJobs();
    },
    [filevinePushState, selectedDocIds, refreshTrackedJobs],
  );

  // Open a case by its id (metadata path) — used after linking / for the
  // "already linked" jump, where we have a case_id but not a DatasetListItem.
  const openCaseById = useCallback((caseId: string) => {
    setSelectedDataset(caseId);
    setActiveStatus(null);
    setActiveCategory(null);
    setSearchQuery('');
    setSortOption('nominal');
    setDocumentsPaginationModel({ page: 0, pageSize: DEFAULT_DOCUMENT_PAGE_SIZE });
    setSelectedDocumentId(null);
    setDocumentThumbnails({});
    thumbnailRequests.current.clear();
    setView('overview');
  }, []);

  const handleResolveFilevineProject = useCallback(
    async (params: { projectId?: number; name?: string }) => {
      const r = await apiService.resolveFilevineProject(params);
      return {
        projectId: r.project_id,
        projectName: r.project_name,
        number: r.number,
        alreadyLinkedCaseId: r.already_linked_case_id,
      };
    },
    [],
  );

  const handleSearchFilevineProjects = useCallback(async (query: string) => {
    const r = await apiService.searchFilevineProjects(query);
    return r.projects.map(p => ({
      projectId: p.project_id,
      projectName: p.project_name,
      number: p.number,
      phaseName: p.phase_name,
      isArchived: p.is_archived,
      alreadyLinkedCaseId: p.already_linked_case_id,
    }));
  }, []);

  const handleLinkFilevineProject = useCallback(
    async (params: {
      projectId: number;
      projectName: string;
      caseName?: string;
      useProjectName: boolean;
    }) => {
      const res = await apiService.linkFilevineProject(params);
      // New case exists; refresh the hub list so it appears.
      setDatasetsVersion(prev => prev + 1);
      return res.case_id;
    },
    [],
  );

  const handleBackToDatasets = useCallback(() => {
    setView('datasets');
    setSelectedDocumentId(null);
    setDocumentDetail(null);
    setDetailError(null);
    setDetailWarnings([]);
    setSelectedPageIds([]);
    lastSelectedPageIdRef.current = null;
  }, []);

  const handleBackToOverview = useCallback(() => {
    setView('overview');
    setSelectedDocumentId(null);
    setDocumentDetail(null);
    setDetailWarnings([]);
    setDetailError(null);
    setSelectedPageIds([]);
    lastSelectedPageIdRef.current = null;
  }, []);

  const applyPageSelection = useCallback(
    (ids: string[], anchorId?: string | null, activeId?: string | null) => {
      if (!documentDetail) {
        setSelectedPageIds([]);
        lastSelectedPageIdRef.current = null;
        debugSelection('applyPageSelection skipped (no detail)', { ids });
        return;
      }

      const orderIndex = new Map(documentDetail.pageRail.map((page, index) => [page.id, index]));
      const uniqueIds = Array.from(new Set(ids));
      const knownIds = uniqueIds.filter(id => orderIndex.has(id));
      const sortedKnown = knownIds.sort((a, b) => (orderIndex.get(a) ?? 0) - (orderIndex.get(b) ?? 0));
      const nextSelected = sortedKnown.length > 0 ? sortedKnown : uniqueIds;

      debugSelection('applyPageSelection computed', {
        ids,
        uniqueIds,
        knownIds,
        sortedKnown,
        nextSelected,
      });

      setSelectedPageIds(nextSelected);

      setDocumentDetail(prev => {
        if (!prev) {
          return prev;
        }
        const selectionSet = new Set(nextSelected);
        let changed = false;
        const nextPageRail = prev.pageRail.map(page => {
          const isSelected = selectionSet.has(page.id);
          if (page.selected === isSelected) {
            return page;
          }
          changed = true;
          return { ...page, selected: isSelected };
        });
        return changed ? { ...prev, pageRail: nextPageRail } : prev;
      });

      if (nextSelected.length > 0) {
        if (activeId && nextSelected.includes(activeId)) {
          lastSelectedPageIdRef.current = activeId;
        } else if (anchorId && nextSelected.includes(anchorId)) {
          lastSelectedPageIdRef.current = anchorId;
        } else {
          lastSelectedPageIdRef.current = nextSelected[nextSelected.length - 1];
        }
      } else {
        lastSelectedPageIdRef.current = null;
      }

      debugSelection('applyPageSelection final', {
        ids,
        nextSelected,
        anchorId: anchorId ?? null,
        activeId: activeId ?? null,
        lastSelected: lastSelectedPageIdRef.current,
      });
    },
    [documentDetail],
  );

  // Checkbox toggle on a page item. Without shift: invert that one page.
  // With shift: extend the range from the last toggled anchor to this page
  // and union into the existing selection (sticky shift-select).
  const handleToggleSelect = useCallback(
    (pageId: string, extend: boolean) => {
      if (!documentDetail) return;
      const pages = documentDetail.pageRail;
      const indexLookup = new Map(pages.map((page, index) => [page.id, index]));
      if (!indexLookup.has(pageId)) return;

      const currentSet = new Set(selectedPageIds);

      if (extend) {
        const anchorId = lastSelectedPageIdRef.current
          ?? (selectedPageIds.length > 0 ? selectedPageIds[selectedPageIds.length - 1] : null);
        if (anchorId && indexLookup.has(anchorId)) {
          const anchorIndex = indexLookup.get(anchorId)!;
          const targetIndex = indexLookup.get(pageId)!;
          const start = Math.min(anchorIndex, targetIndex);
          const end = Math.max(anchorIndex, targetIndex);
          for (let i = start; i <= end; i += 1) {
            currentSet.add(pages[i].id);
          }
          applyPageSelection(Array.from(currentSet), anchorId, pageId);
          return;
        }
      }

      if (currentSet.has(pageId)) {
        currentSet.delete(pageId);
      } else {
        currentSet.add(pageId);
      }
      applyPageSelection(Array.from(currentSet), pageId, pageId);
    },
    [documentDetail, selectedPageIds, applyPageSelection],
  );

  // Replace the current selection wholesale (used by the rail's selection pill).
  const handleSetPageSelection = useCallback(
    (pageIds: string[]) => {
      const last = pageIds.length > 0 ? pageIds[pageIds.length - 1] : null;
      applyPageSelection(pageIds, last, last);
    },
    [applyPageSelection],
  );

  useEffect(() => {
    debugSelection('selectedPageIds state change', selectedPageIds);
  }, [selectedPageIds]);

  const activeDataset = useMemo(
    () => datasets.find(item => item.metadataPath === selectedDataset) ?? null,
    [datasets, selectedDataset],
  );

  const lastModifiedLabel = useMemo(() => {
    if (!activeDataset || !activeDataset.lastModified) {
      return null;
    }
    const timestampMillis = activeDataset.lastModified * 1000;
    if (!Number.isFinite(timestampMillis) || timestampMillis <= 0) {
      return null;
    }
    return formatLocalDateTime(new Date(timestampMillis));
  }, [activeDataset]);

  const handleExportDocument = useCallback(
    async (docId: string) => {
      if (!selectedDataset) {
        return;
      }
      try {
        const response = await apiService.exportDocumentPDF(selectedDataset, docId);
        const url = URL.createObjectURL(response.blob);
        const a = document.createElement('a');
        a.href = url;
        // Use filename from server response if available, otherwise fall back to docId
        a.download = response.filename || `${docId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Export document failed', error);
        alert('Failed to export document');
      }
    },
    [selectedDataset],
  );

  const handleDownloadPDFs = useCallback(async () => {
    if (!selectedDataset || isExporting) return;
    setIsExporting(true);
    try {
      const { job_id } = await apiService.exportCasePDF(selectedDataset);
      const result = await pollJob(job_id);
      const archivePath = (result.result as any)?.archive_path;
      if (!archivePath) {
        alert('Export completed but no archive was produced.');
        return;
      }
      const filename = `${getCaseDisplayTitle(activeDataset) ?? 'case'}_export.zip`;
      const a = document.createElement('a');
      a.href = `/download/${archivePath}?filename=${encodeURIComponent(filename)}`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error: any) {
      if (!error?.cancelled) {
        alert(`Download failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    } finally {
      setIsExporting(false);
    }
  }, [selectedDataset, isExporting, pollJob, activeDataset]);

  const overviewSummary = useMemo(() => {
    const stats = summary?.summary ?? EMPTY_SUMMARY;
    const actions: SummaryAction[] = [];

    if (selectedDataset) {
      actions.push({
        id: 'download-pdfs',
        label: isExporting ? 'Preparing\u2026' : 'Download PDFs',
        icon: <Download size={16} />,
        onClick: handleDownloadPDFs,
        disabled: isExporting || !stats.totalDocuments,
        variant: 'primary',
      });

      actions.push({
        id: 'reprocess-dataset',
        label: 'Re-run analysis\u2026',
        icon: <RotateCcw size={16} />,
        onClick: openDatasetReprocess,
      });

      // Only offered for cases linked to a Filevine project: the pull appends
      // the chosen document into THIS case (download -> boundary-split -> review).
      if (typeof summary?.filevineProjectId === 'number') {
        actions.push({
          id: 'filevine-pull',
          label: 'Pull from Filevine\u2026',
          icon: <Download size={16} />,
          onClick: () => setFilevinePullState({ metadataPath: selectedDataset }),
        });
        // Push this case's exported documents back to the linked project.
        // (Server-side gates still apply: with push disabled or a project not
        // on the allowlist, the job dry-runs to a local preview.)
        actions.push({
          id: 'filevine-push',
          label: 'Push to Filevine\u2026',
          icon: <Upload size={16} />,
          onClick: () => setFilevinePushState({ metadataPath: selectedDataset }),
          disabled: !stats.totalDocuments,
        });
      }
    }

    const hasActiveCase = Boolean(selectedDataset);
    const summarySubtitle = hasActiveCase
      ? undefined
      : summaryError
        ? undefined
        : 'Choose a case to load its documents';

    const headerSummary = {
      title: hasActiveCase ? (getCaseDisplayTitle(activeDataset) ?? 'Case') : summary?.title ?? 'Select a case to begin',
      subtitle: hasActiveCase ? undefined : summarySubtitle,
      stats,
      reviewStatusLabel: summary?.reviewStatusLabel,
      lastUpdatedAt: summary?.lastUpdatedAt,
      lastUpdatedBy: summary?.lastUpdatedBy,
      actions,
      datasetPath: selectedDataset ?? undefined,
      onDuplicatesClick: selectedDataset && stats.duplicatePairs > 0
        ? () => setDuplicatesModalState({ datasetPath: selectedDataset })
        : undefined,
      filevineProjectId: summary?.filevineProjectId ?? null,
      filevineProjectName: summary?.filevineProjectName ?? null,
    };

    return headerSummary;
  }, [
    summary,
    summaryError,
    selectedDataset,
    openDatasetReprocess,
    activeDataset,
    handleDownloadPDFs,
    isExporting,
    debugMode,
  ]);

  const combinedWarnings = useMemo(() => {
    const warnings = new Set<string>();
    activeDataset?.warnings.forEach(w => warnings.add(w));
    summary?.warnings.forEach(w => warnings.add(w));
    documentWarnings.forEach(w => warnings.add(w));
    if (view === 'inspect') {
      detailWarnings.forEach(w => warnings.add(w));
    }
    return Array.from(warnings);
  }, [activeDataset, summary, documentWarnings, detailWarnings, view]);

  const statusItemsForDatasets = useMemo<StatusBarItem[]>(() => {
    const items: StatusBarItem[] = [];
    if (datasetsError) {
      items.push({ id: 'datasets-error', tone: 'danger', message: datasetsError });
    }
    if (jobsError) {
      items.push({ id: 'jobs-error', tone: 'danger', message: jobsError });
    }
    return items;
  }, [datasetsError, jobsError]);

  const statusItemsForOverview = useMemo<StatusBarItem[]>(() => {
    const items: StatusBarItem[] = [];
    if (summaryError) {
      items.push({ id: 'summary-error', tone: 'danger', message: summaryError });
    }
    if (documentsError) {
      items.push({ id: 'documents-error', tone: 'danger', message: documentsError });
    }
    return items;
  }, [isLoadingSummary, summaryError, documentsError]);

  const statusItemsForInspect = useMemo<StatusBarItem[]>(() => {
    const items: StatusBarItem[] = [];
    if (detailError) {
      items.push({ id: 'detail-error', tone: 'danger', message: detailError });
    }
    if (detailActionError) {
      items.push({ id: 'detail-action-error', tone: 'danger', message: detailActionError });
    }
    if (detailActionMessage) {
      items.push({ id: 'detail-action', tone: 'success', message: detailActionMessage });
    }
    return items;
  }, [detailError, detailActionError, detailActionMessage]);

  const mapTagStrings = useCallback(
    (tags: string[]): DocumentTag[] => tags.map(tag => ({ id: tag, label: tag, tone: 'default' })),
    [],
  );

  const applyOverviewUpdate = useCallback(
    (updated: DocumentOverviewItem) => {
      setDocuments(prev =>
        prev.map(doc => (doc.docId === updated.docId ? { ...doc, ...updated } : doc)),
      );
      setDocumentThumbnails(prev => {
        if (!updated.thumbnailUrls) {
          return prev;
        }
        return {
          ...prev,
          [updated.docId]: updated.thumbnailUrls,
        };
      });
    },
    [],
  );

  const updateDocument = useCallback(
    async (
      docId: string,
      payload: UpdateDocumentPayload,
      options: { successMessage?: string } = {},
    ) => {
      console.debug('[UpdateDocument] start', {
        docId,
        payload,
        options,
        selectedDataset,
      });
      if (!selectedDataset) {
        console.warn('Cannot update document without a selected case.');
        return;
      }
      const isActiveDetail = documentDetail?.header.documentId === docId;
      const previousDoc = documents.find(doc => doc.docId === docId) ?? null;
      const previousDetail = documentDetail;

      const applyOptimisticOverview = (doc: DocumentOverviewItem): DocumentOverviewItem => {
        let next = doc;
        let changed = false;
        if (payload.title !== undefined && payload.title !== doc.title) {
          next = changed ? next : { ...next };
          changed = true;
          next.title = payload.title ?? doc.title;
        }
        // status is DERIVED server-side from confirmations — not optimistically
        // updatable here; the post-update refetch reflects the new derived value.
        if (payload.tags !== undefined) {
          next = changed ? next : { ...next };
          changed = true;
          next.tags = mapTagStrings(payload.tags);
        }
        if (payload.date !== undefined) {
          next = changed ? next : { ...next };
          changed = true;
          next.date = payload.date ?? undefined;
        }
        return next;
      };

      const applyOptimisticDetail = (detail: DocumentDetailData): DocumentDetailData => {
        let header = detail.header;
        if (payload.title !== undefined && payload.title !== header.documentTitle) {
          header = { ...header, documentTitle: payload.title };
        }
        // status is DERIVED server-side — refetched after the update, not set here.
        if (payload.tags !== undefined) {
          header = { ...header, tags: payload.tags };
        }
        if (payload.date !== undefined) {
          header = { ...header, date: payload.date ?? undefined };
        }
        // Category edit/confirm: the authoritative taxonomy comes back from the
        // server (with display_label + canonical_rank), so we don't optimistically
        // fabricate it here — we just clear a stale "needs review" badge if the
        // user is actively setting/confirming a category.
        if ((payload.taxonomy !== undefined || payload.confirmTaxonomy) && header.taxonomy) {
          header = {
            ...header,
            taxonomy: { ...header.taxonomy, classificationFailed: false },
          };
        }

        return detail.header === header ? detail : { ...detail, header };
      };

      setDocuments(prev => prev.map(doc => (doc.docId === docId ? applyOptimisticOverview(doc) : doc)));

      if (isActiveDetail && documentDetail) {
        setIsUpdatingDetail(true);
        setDocumentDetail(prev => (prev ? applyOptimisticDetail(prev) : prev));
        setDetailActionError(null);
        setDetailActionMessage(null);
      }

      try {
        const updated = await apiService.updateDocument(selectedDataset, docId, payload);
        console.debug('[UpdateDocument] success', {
          docId,
          responseTitle: updated.title,
          response: updated,
        });
        applyOverviewUpdate(updated);

        if (isActiveDetail) {
          setDocumentDetail(prev => {
            if (!prev) {
              return prev;
            }
            const updatedTags = updated.tags ? updated.tags.map(tag => tag.label) : prev.header.tags;
            const nextHeader = {
              ...prev.header,
              documentTitle: updated.title ?? prev.header.documentTitle,
              status: updated.status,
              tags: updatedTags,
              date: updated.date ?? prev.header.date,
              taxonomy: updated.taxonomy ?? prev.header.taxonomy,
            };
            return { ...prev, header: nextHeader };
          });

          if (options.successMessage) {
            setDetailActionMessage(options.successMessage);
          }
        }

        return updated;
      } catch (error) {
        console.error('Failed to update document', error);
        if (previousDoc) {
          setDocuments(prev =>
            prev.map(doc => (doc.docId === docId ? previousDoc : doc)),
          );
        }
        if (isActiveDetail) {
          setDocumentDetail(previousDetail);
          setDetailActionError('Failed to save document changes.');
        }
        throw error;
      } finally {
        if (isActiveDetail) {
          setIsUpdatingDetail(false);
        }
      }
    },
    [applyOverviewUpdate, documentDetail, documents, selectedDataset],
  );

  // Review status is DERIVED from confirming title/date/category — there is no
  // direct status setter (handleDocumentStatusChange / handleMarkReviewed were
  // removed). Confirm the three fields via handleConfirmTitle/Date/Category.

  const handleDocumentRename = useCallback(
    async (docId: string, nextTitle: string) => {
      console.debug('[DocumentRename] requested', { docId, nextTitle });
      const trimmed = nextTitle.trim();
      if (!trimmed) {
        if (documentDetail?.header.documentId === docId) {
          setDetailActionError('Title cannot be empty.');
        }
        return;
      }

      const existing = documents.find(doc => doc.docId === docId);
      if (existing && existing.title.trim() === trimmed) {
        return;
      }

      await updateDocument(docId, { title: trimmed }, {
        successMessage: documentDetail?.header.documentId === docId ? 'Title updated.' : undefined,
      });

      if (documentDetail?.header.documentId === docId) {
        setDetailVersion(prev => prev + 1);
      }
    },
    [documents, documentDetail, updateDocument],
  );

  const mergeTagPool = useCallback((nextTags: string[]) => {
    setTagPool(prev => {
      const combined = [...prev, ...nextTags];
      const seen = new Set<string>();
      const result: string[] = [];
      combined.forEach(tag => {
        const trimmed = tag.trim();
        if (!trimmed) {
          return;
        }
        const key = trimmed.toLowerCase();
        if (seen.has(key)) {
          return;
        }
        seen.add(key);
        result.push(trimmed);
      });
      return result.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    });
  }, []);

  const handleDocumentTagsChange = useCallback(
    async (docId: string, nextTags: string[]) => {
      const cleaned = nextTags.map(tag => tag.trim()).filter(tag => tag);
      await updateDocument(
        docId,
        { tags: cleaned },
        { successMessage: documentDetail?.header.documentId === docId ? 'Tags updated.' : undefined },
      );
      mergeTagPool(cleaned);
      if (documentDetail?.header.documentId === docId) {
        setDetailVersion(prev => prev + 1);
      }
    },
    [documentDetail, mergeTagPool, updateDocument],
  );

  const handleDocumentDateChange = useCallback(
    async (docId: string, nextDate: DocumentDate | null) => {
      await updateDocument(
        docId,
        { date: nextDate },
        { successMessage: documentDetail?.header.documentId === docId ? 'Date updated.' : undefined },
      );
      if (documentDetail?.header.documentId === docId) {
        setDetailVersion(prev => prev + 1);
      }
    },
    [documentDetail, updateDocument],
  );

  // Approve the current date as already correct (review-confirm), recording the
  // reviewer canonically server-side — mirrors handleConfirmCategory.
  const handleConfirmDate = useCallback(
    async (docId: string) => {
      await updateDocument(
        docId,
        { confirmDate: true },
        { successMessage: documentDetail?.header.documentId === docId ? 'Date confirmed.' : undefined },
      );
      if (documentDetail?.header.documentId === docId) {
        setDetailVersion(prev => prev + 1);
      }
    },
    [documentDetail, updateDocument],
  );

  // Approve the current title as already correct (review-confirm without
  // renaming). Records the reviewer canonically server-side — mirrors
  // handleConfirmDate/handleConfirmCategory. One of the three confirmations
  // that derive review status.
  const handleConfirmTitle = useCallback(
    async (docId: string) => {
      await updateDocument(
        docId,
        { confirmTitle: true },
        { successMessage: documentDetail?.header.documentId === docId ? 'Title confirmed.' : undefined },
      );
      if (documentDetail?.header.documentId === docId) {
        setDetailVersion(prev => prev + 1);
      }
    },
    [documentDetail, updateDocument],
  );

  // Manual category override — the user's choice is implicitly correct and
  // stored as the authoritative (clobber-safe) taxonomy_confirmed server-side.
  const handleDocumentCategoryChange = useCallback(
    async (docId: string, next: { section: string; leaf?: string | null; subLeaf?: string | null }) => {
      await updateDocument(
        docId,
        { taxonomy: next },
        { successMessage: documentDetail?.header.documentId === docId ? 'Category updated.' : undefined },
      );
      if (documentDetail?.header.documentId === docId) {
        setDetailVersion(prev => prev + 1);
      }
    },
    [documentDetail, updateDocument],
  );

  // Approve the current auto category as already correct (review-confirm).
  const handleConfirmCategory = useCallback(
    async (docId: string) => {
      await updateDocument(
        docId,
        { confirmTaxonomy: true },
        { successMessage: documentDetail?.header.documentId === docId ? 'Category confirmed.' : undefined },
      );
      if (documentDetail?.header.documentId === docId) {
        setDetailVersion(prev => prev + 1);
      }
    },
    [documentDetail, updateDocument],
  );

  const handleCreateTag = useCallback(
    async (name: string) => {
      if (!selectedDataset) {
        return;
      }
      const trimmed = name.trim();
      if (!trimmed) {
        return;
      }
      const response = await apiService.createDatasetTag(selectedDataset, trimmed);
      setTagPool(response);
    },
    [selectedDataset],
  );

  const handleWarningClick = useCallback(
    (warningId: string, docId: string) => {
      console.debug('[WarningClick]', { warningId, docId });

      // Check if this is a duplicate warning
      if (warningId.includes('duplicate') || warningId.includes('Possible duplicate')) {
        if (selectedDataset) {
          setDuplicatesModalState({
            datasetPath: selectedDataset,
            documentId: docId,
          });
        }
      }
    },
    [selectedDataset],
  );

  // No header-level review action remains: status is DERIVED from confirming
  // title/date/category (the old "Mark reviewed" button was removed). Kept as a
  // no-op so any future header action can be dispatched here; save-now and the
  // removed mark-reviewed are already filtered out of the visible action list.
  const handleHeaderAction = useCallback((_actionId: string) => {}, []);

  const handleMergeAdjacent = useCallback(
    async (adjacentDocId: string, direction: 'before' | 'after') => {
      if (!selectedDataset || !documentDetail) return;
      setIsUpdatingDetail(true);
      try {
        await apiService.performBulkAction(
          selectedDataset,
          documentDetail.header.documentId,
          {
            action: 'merge',
            pageIds: [],
            payload: {
              source_doc_ids: [adjacentDocId],
              position: direction === 'before' ? 'start' : undefined,
            },
          },
        );
        setDetailVersion(v => v + 1);
        await refreshOverviewDocuments();
      } catch (error: any) {
        setDetailActionError(`Merge failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        setIsUpdatingDetail(false);
      }
    },
    [selectedDataset, documentDetail, refreshOverviewDocuments],
  );

  // Doc-level merge from the document list. The survivor absorbs all pages
  // from the source docs (the backend sorts sources by case order and
  // appends in that order, then prunes the now-empty source docs). This is
  // the entry point for the multi-select merge action in the overview.
  const [isMergingDocs, setIsMergingDocs] = useState(false);
  const handleMergeFromList = useCallback(
    async (
      survivorDocId: string,
      sourceDocIds: string[],
      options?: { title?: string },
    ): Promise<boolean> => {
      if (!selectedDataset || sourceDocIds.length === 0) return false;
      setIsMergingDocs(true);
      try {
        const trimmedTitle = options?.title?.trim();
        const payload: Record<string, unknown> = { source_doc_ids: sourceDocIds };
        if (trimmedTitle) payload.title = trimmedTitle;
        await apiService.performBulkAction(
          selectedDataset,
          survivorDocId,
          {
            action: 'merge',
            pageIds: [],
            payload,
          },
        );
        await refreshOverviewDocuments();
        return true;
      } catch (error: any) {
        alert(`Merge failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        return false;
      } finally {
        setIsMergingDocs(false);
      }
    },
    [selectedDataset, refreshOverviewDocuments],
  );

  const handleCloseMergeModal = useCallback(() => {
    if (isMergingDocs) return;  // don't dismiss mid-merge
    setMergeModalOpen(false);
    setMergeSurvivorDocId(null);
  }, [isMergingDocs]);

  const handleConfirmMerge = useCallback(async (title: string) => {
    if (!mergeSurvivorDocId) return;
    const sourceIds = selectedDocs
      .map(d => d.docId)
      .filter(id => id !== mergeSurvivorDocId);
    if (sourceIds.length === 0) return;
    const ok = await handleMergeFromList(mergeSurvivorDocId, sourceIds, { title });
    if (ok) {
      // Clear selection on success — the source docs no longer exist and
      // the survivor's identity is unchanged but its content has shifted,
      // so a fresh selection makes more sense than carrying it forward.
      setSelectedDocIds([]);
      setSelectionAnchorDocId(null);
      setSelectedDocCache({});
      setMergeModalOpen(false);
      setMergeSurvivorDocId(null);
    }
  }, [mergeSurvivorDocId, selectedDocs, handleMergeFromList]);

  const executeBulkAction = useCallback(
    async (
      actionKey: 'move' | 'new_document' | 'split' | 'remove_pages' | 'rotate_pages',
      options: {
        pageIds?: string[];
        targetDocId?: string | null;
        payload?: Record<string, unknown>;
        successMessage?: string;
        preserveSelection?: boolean;
      } = {},
    ) => {
      if (!selectedDataset || !documentDetail) {
        setDetailActionError('Select a document before performing actions.');
        return;
      }
      const pageIds = options.pageIds ?? selectedPageIds;
      if (!pageIds.length) {
        setDetailActionError('Select at least one page before performing this action.');
        return;
      }

      setIsUpdatingDetail(true);
      setDetailActionError(null);
      setDetailActionMessage(null);

      const currentDocId = documentDetail.header.documentId;
      const payload = { ...(options.payload ?? {}) };

      debugSelection('executeBulkAction request', {
        actionKey,
        pageIds,
        payload,
        targetDocId: options.targetDocId ?? null,
      });

      try {
        const response = await apiService.performBulkAction(selectedDataset, currentDocId, {
          action: actionKey,
          pageIds,
          targetDocId: options.targetDocId ?? undefined,
          payload,
        });

        const warnings = response.warnings ?? [];
        debugSelection('executeBulkAction response', {
          actionKey,
          updatedDocumentIds: response.updatedDocumentIds ?? [],
          warnings,
        });
        if (warnings.includes('bulk_action_unsupported_schema')) {
          setDetailActionError('Bulk actions are only available for linked cases.');
          return;
        }
        const docRemoved = warnings.some(warning => warning === `doc_removed:${currentDocId}`);

        await refreshOverviewDocuments();

        const message = options.successMessage
          ?? (() => {
            switch (actionKey) {
              case 'move':
                return 'Pages moved.';
              case 'new_document':
                return 'Created a new document from selection.';
              case 'split':
                return 'Document split.';
              case 'remove_pages':
                return 'Removed selected pages.';
              case 'rotate_pages':
                return 'Rotated selected pages.';
              default:
                return 'Bulk action completed.';
            }
          })();

        if (docRemoved) {
          applyPageSelection([], null, null);
          handleBackToOverview();
          setDetailActionMessage(message);
          return;
        }

        if (actionKey === 'new_document') {
          const candidates = response.updatedDocumentIds ?? [];
          const createdDocId = candidates.find(id => id && id !== currentDocId) ?? null;

          if (createdDocId) {
            debugSelection('executeBulkAction new document detected', {
              createdDocId,
              candidates,
            });
            setSelectedDocumentId(createdDocId);
            setDocumentDetail(null);
            setSelectedPageIds([]);
            lastSelectedPageIdRef.current = null;
            setView('inspect');
            setDetailActionMessage(message);
            return;
          }
          debugSelection('executeBulkAction new document not detected', {
            candidates,
            currentDocId,
          });
        }

        setDetailVersion(prev => prev + 1);
        if (!options.preserveSelection) {
          applyPageSelection([], null, null);
        }
        setDetailActionMessage(message);
      } catch (error) {
        console.error('Failed to apply bulk action', error);
        setDetailActionError('Failed to apply bulk action.');
      } finally {
        setIsUpdatingDetail(false);
      }
    },
    [
      selectedDataset,
      documentDetail,
      selectedPageIds,
      refreshOverviewDocuments,
      applyPageSelection,
      handleBackToOverview,
      setSelectedDocumentId,
      setDocumentDetail,
      setSelectedPageIds,
      setView,
    ],
  );

  // Rail drag-and-drop reorder within the current document. The pages move as
  // one contiguous chunk to sit before/after the anchor page. Uses the `move`
  // bulk action, which stays in the current document (no target_doc_id) and
  // whose anchor resolution skips the moving pages so the chunk lands intact.
  const handleReorderPages = useCallback(
    (pageIds: string[], anchorPageId: string, position: 'before' | 'after') => {
      if (!documentDetail || pageIds.length === 0) {
        return;
      }
      void executeBulkAction('move', {
        pageIds,
        payload: position === 'before'
          ? { before_page_id: anchorPageId, preserve_empty_docs: true }
          : { after_page_id: anchorPageId, preserve_empty_docs: true },
        // Keep the moved pages selected so the user can keep dragging the chunk.
        preserveSelection: true,
        successMessage: 'Pages reordered.',
      });
    },
    [documentDetail, executeBulkAction],
  );

  // Undo a reorder: move the same pages back to their original position — after
  // `afterPageId`, or to the document start when it's null (the chunk was at the
  // head). Reuses the `move` action, so it lands the chunk intact just like the
  // forward move did.
  const handleUndoReorder = useCallback(
    (pageIds: string[], afterPageId: string | null) => {
      if (!documentDetail || pageIds.length === 0) {
        return;
      }
      void executeBulkAction('move', {
        pageIds,
        payload: afterPageId
          ? { after_page_id: afterPageId, preserve_empty_docs: true }
          : { position: 'start', preserve_empty_docs: true },
        preserveSelection: true,
        successMessage: 'Move undone.',
      });
    },
    [documentDetail, executeBulkAction],
  );

  const handleRemovePages = useCallback(
    (pageIds?: string[]) => {
      if (!documentDetail) {
        return;
      }
      const ids = pageIds ?? selectedPageIds;
      if (!ids.length) {
        setDetailActionError('Select pages to remove.');
        return;
      }
      void executeBulkAction('remove_pages', {
        pageIds: ids,
        payload: { preserve_empty_docs: false },
        successMessage: 'Removed selected pages.',
      });
    },
    [documentDetail, executeBulkAction, selectedPageIds],
  );

  // Splits the current document after `anchorPageId`. Pages with positions
  // strictly greater than the anchor move into a brand-new document inserted
  // immediately after the source. The first-doc title is optionally renamed
  // (when it differs from the current title); the new doc receives the
  // user-supplied second title.
  const handleSplitDocumentAfter = useCallback(
    async (
      anchorPageId: string,
      options: { firstDocTitle: string; secondDocTitle: string },
    ) => {
      if (!documentDetail || !selectedDataset) {
        return;
      }
      const pageRail = documentDetail.pageRail;
      const anchorIndex = pageRail.findIndex(p => p.id === anchorPageId);
      if (anchorIndex < 0) {
        setDetailActionError('Split anchor page not found in current document.');
        return;
      }
      const pagesToMove = pageRail.slice(anchorIndex + 1).map(p => p.id);
      if (pagesToMove.length === 0) {
        setDetailActionError('Cannot split — no pages after the selected page.');
        return;
      }

      const currentDocId = documentDetail.header.documentId;
      const currentTitle = documentDetail.header.documentTitle;
      const firstTitle = options.firstDocTitle.trim();
      const secondTitle = options.secondDocTitle.trim();

      if (firstTitle && firstTitle !== currentTitle) {
        try {
          await updateDocument(currentDocId, { title: firstTitle });
        } catch (error) {
          console.error('Failed to rename original document during split', error);
        }
      }

      void executeBulkAction('split', {
        pageIds: pagesToMove,
        payload: {
          title: secondTitle || undefined,
          anchor_doc_id: currentDocId,
          preserve_empty_docs: true,
        },
        successMessage: 'Document split.',
      });
    },
    [documentDetail, executeBulkAction, selectedDataset, updateDocument],
  );

  const inspectWorkspaceProps = useMemo<DocumentInspectWorkspaceProps | null>(() => {
    if (!documentDetail) {
      return null;
    }

    const encodedDataset = selectedDataset ? encodeURIComponent(selectedDataset) : '';
    const headerData = documentDetail.header;
    const headerActionsMap: Record<string, { label: string; variant: SummaryAction['variant']; icon?: React.ReactNode }> = {};

    const visibleActionIds = headerData.actions.filter(actionId => actionId !== 'save-now' && actionId !== 'mark-reviewed');

    const headerActions: InspectHeaderAction[] = visibleActionIds.map(actionId => {
      const action = headerActionsMap[actionId] ?? {
        label: actionId.replace(/-/g, ' '),
        variant: 'secondary',
      };
      return {
        ...action,
        id: actionId,
        disabled: isUpdatingDetail,
        onClick: () => handleHeaderAction(actionId),
      };
    });

    const headerProps: DocumentInspectHeaderProps = {
      documentTitle: headerData.documentTitle,
      documentId: headerData.documentId,
      taxonomy: headerData.taxonomy ?? null,
      date: headerData.date,
      pageCount: documentDetail.pageRail.length,
      actions: headerActions,
      activeEditors: headerData.activeEditors ?? [],
      currentEditorId: presenceEditorId,
      onRenameSubmit: (value: string) => handleDocumentRename(headerData.documentId, value),
      titleConfirmation: headerData.titleConfirmation ?? null,
      onTitleConfirm: () => handleConfirmTitle(headerData.documentId),
      onDateChange: (nextDate: DocumentDate | null) => handleDocumentDateChange(headerData.documentId, nextDate),
      onDateConfirm: () => handleConfirmDate(headerData.documentId),
      categoryTree,
      onCategoryChange: (next) => handleDocumentCategoryChange(headerData.documentId, next),
      onCategoryConfirm: () => handleConfirmCategory(headerData.documentId),
      isTitleSaving: isUpdatingDetail,
      isDuplicateOfOtherDoc: documents.find(d => d.docId === headerData.documentId)?.isDuplicateOfOtherDoc ?? false,
      duplicateOfRatio: documents.find(d => d.docId === headerData.documentId)?.duplicateOfRatio ?? null,
      duplicatePageCount: documents.find(d => d.docId === headerData.documentId)?.pageCount ?? 0,
      onDuplicateBadgeClick: () => {
        setDuplicateFilterDocId(headerData.documentId);
        setSelectedDocumentId(null);
        setView('overview');
      },
    };

    // Derived, read-only status (no setter — it follows from confirming
    // title/date/category).
    const documentStatusConfig = {
      status: headerData.status,
    };

    const selectionCount = selectedPageIds.length;

    const pageRailProps: DocumentPageRailProps = {
      pages: documentDetail.pageRail.map(item => ({
        ...item,
        selected: selectedPageIds.includes(item.id),
        thumbnailUrl:
          item.thumbnailUrl ||
          (item.pageIndex != null && encodedDataset
            ? `/pages/thumb/${item.pageIndex}?metadata=${encodedDataset}`
            : item.thumbnailUrl),
      })),
      selectedCount: selectionCount,
    };

    // Page-level actions surfaced via the canvas header dropdown. The menu
    // itself is always shown; the workspace decides which individual items
    // are enabled based on the current selection.
    const pageActions: DocumentPageActions = {
      disabled: isUpdatingDetail,
      onDeletePages: ids => handleRemovePages(ids),
      onSplitAfter: (anchorId, opts) => {
        void handleSplitDocumentAfter(anchorId, opts);
      },
      onExportDocument: () => void handleExportDocument(headerData.documentId),
      onReprocessDocument: () => openDocumentReprocess(headerData.documentId, headerData.documentTitle),
    };

    const canvasSource = new Map(
      documentDetail.canvas.map(page => [page.pageId, page]),
    );

    const viewerPages: CanvasPage[] = documentDetail.pageRail.map(page => {
      const canvasPage = canvasSource.get(page.id);
      const derivedLabel = page.label ?? `Page ${page.pageNumber}`;
      let imageUrl = canvasPage?.imageUrl ?? null;
      if (!imageUrl) {
        imageUrl =
          page.pageIndex != null && encodedDataset
            ? `/pages/full/${page.pageIndex}?metadata=${encodedDataset}`
            : placeholderImage(derivedLabel);
      }

      return {
        id: page.id,
        label: derivedLabel,
        pageNumber: page.pageNumber,
        imageUrl,
        width: canvasPage?.width ?? null,
        height: canvasPage?.height ?? null,
        warnings: canvasPage?.warnings ?? page.warnings,
        notes: page.notes ?? canvasPage?.notes ?? [],
        sourcePdf: page.sourcePdf ?? null,
        sourcePageIndex: page.sourcePageIndex ?? null,
        boundaryAfter: page.boundaryAfter ?? null,
        notesCount: page.notesCount,
        reorderStatus: page.reorderStatus ?? null,
        printedPageNumber: page.printedPageNumber ?? null,
        internalDuplicate: page.internalDuplicate ?? false,
        externalDuplicate: page.externalDuplicate ?? false,
      };
    });

    return {
      header: headerProps,
      documentStatus: documentStatusConfig,
      pageRail: pageRailProps,
      pages: viewerPages,
      ocrExcerpts: documentDetail.ocrExcerpts,
      searchQuery: searchQuery || undefined,
      onSearchQueryChange: setSearchQuery,
      previousDocument: documentDetail.previousDocument ? {
        ...documentDetail.previousDocument,
        pages: documentDetail.previousDocument.pages.map(p => ({
          ...p,
          // Prefer the backend's identity-keyed URL; index-based fallback for
          // older responses only.
          thumbnailUrl: p.thumbnailUrl
            || (p.pageIndex != null && encodedDataset
              ? `/pages/thumb/${p.pageIndex}?metadata=${encodedDataset}`
              : undefined),
        })),
      } : undefined,
      nextDocument: documentDetail.nextDocument ? {
        ...documentDetail.nextDocument,
        pages: documentDetail.nextDocument.pages.map(p => ({
          ...p,
          thumbnailUrl: p.thumbnailUrl
            || (p.pageIndex != null && encodedDataset
              ? `/pages/thumb/${p.pageIndex}?metadata=${encodedDataset}`
              : undefined),
        })),
      } : undefined,
      onMergeAdjacent: handleMergeAdjacent,
      onNavigateToDocument: handleDocumentOpen,
      duplicateFilterDocId: duplicateFilterDocId ?? undefined,
      onClearDuplicateFilter: () => setDuplicateFilterDocId(null),
      pageActions,
      onSetSelection: handleSetPageSelection,
      onToggleSelect: handleToggleSelect,
      onReorderPages: handleReorderPages,
      onUndoReorder: handleUndoReorder,
      duplicateData: documentDuplicates,
      onNavigateToDuplicate: handleDocumentOpen,
      getThumbnailUrl: (pageId: string, pageIndex?: number) => {
        // If pageIndex is provided directly (from duplicate data), use it
        if (pageIndex != null && encodedDataset) {
          const url = `/pages/full/${pageIndex}?metadata=${encodedDataset}`;
          console.log('[getThumbnailUrl] Using provided pageIndex for', pageId, ':', url);
          return url;
        }

        // Otherwise, look it up in the current document's page rail
        const page = documentDetail.pageRail.find(p => p.id === pageId);
        if (!page || !encodedDataset || page.pageIndex == null) {
          console.log('[getThumbnailUrl] Could not find page for pageId:', pageId);
          return null;
        }
        // Use full-size images for duplicate comparison (better quality)
        const url = `/pages/full/${page.pageIndex}?metadata=${encodedDataset}`;
        console.log('[getThumbnailUrl] Found pageIndex via lookup for', pageId, ':', url);
        return url;
      },
    };
  }, [
    documentDetail,
    documentDuplicates,
    mapTagStrings,
    selectedDataset,
    handleHeaderAction,
    handleConfirmTitle,
    handleDocumentRename,
    isUpdatingDetail,
    openDocumentReprocess,
    presenceEditorId,
    selectedPageIds,
    handleSetPageSelection,
    handleToggleSelect,
    handleReorderPages,
    handleUndoReorder,
    handleRemovePages,
    handleSplitDocumentAfter,
    debugMode,
    handleExportDocument,
    handleDocumentOpen,
    searchQuery,
    categoryTree,
    handleDocumentCategoryChange,
    handleConfirmCategory,
  ]);

  const caseSubtitle = useMemo(() => {
    if (!activeDataset || !lastModifiedLabel) {
      return undefined;
    }
    return `Updated ${lastModifiedLabel}`;
  }, [activeDataset, lastModifiedLabel]);

  const emptyOverviewMessage = documentsError
    ? documentsError
    : isLoadingDocuments
      ? 'Loading documents…'
      : 'No documents match the current filters.';


  const uploadModal = (
    <UploadModal
      visible={isUploadModalVisible}
      onClose={handleCloseUploadModal}
      onCaseCreated={handleCaseCreated}
      uploadDisabled={false}
    />
  );

  // Compute the conflict shown by the reprocess modal. Mirrors the
  // server's find_active_match rules: any PENDING/RUNNING/CANCELLING
  // inference or case-wide reprocess on this dataset blocks a new
  // re-analysis; a doc-scoped re-analysis is also blocked if the same
  // document already has one queued or running.
  const reprocessConflict = (() => {
    if (!reprocessModalState) return null;
    const targetDataset = (() => {
      const parts = reprocessModalState.metadataPath.split('/').filter(Boolean);
      return parts.slice(0, -1).join('/');
    })();
    const ACTIVE = new Set(['PENDING', 'RUNNING', 'CANCELLING']);
    const conflicts = Object.values(trackedJobs).filter(j =>
      ACTIVE.has(j.status ?? '') && j.dataset_path === targetDataset,
    );
    const caseWide = conflicts.find(j =>
      j.job_type === 'inference' || j.job_type === 'reprocess_dataset',
    );
    if (caseWide) {
      return { message: 'A re-analysis of this case is already in progress or queued. Wait for it to finish before starting another.' };
    }
    if (reprocessModalState.scope === 'document') {
      const docId = reprocessModalState.documentId;
      const docConflict = conflicts.find(j =>
        j.job_type === 'reprocess_document' && (j.payload as any)?.document_id === docId,
      );
      if (docConflict) {
        return { message: 'A re-analysis of this document is already in progress or queued. Wait for it to finish before starting another.' };
      }
    }
    return null;
  })();

  const reprocessModal = reprocessModalState ? (
    <ReprocessModal
      visible
      scope={reprocessModalState.scope === 'document' ? 'document' : 'case'}
      targetLabel={reprocessModalState.title}
      onConfirm={handleReprocessConfirm}
      onCancel={handleReprocessCancel}
      conflict={reprocessConflict}
    />
  ) : null;

  const deleteModal = (
    <ModalOverlay
      $visible={Boolean(casePendingDelete)}
      onClick={handleCancelDeleteCase}
      role="presentation"
    >
      {casePendingDelete && (
        <ModalPanel
          $width="440px"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="delete-case-title"
          aria-describedby="delete-case-message"
          onClick={event => event.stopPropagation()}
        >
          <ModalTitle id="delete-case-title">Delete case?</ModalTitle>
          <div id="delete-case-message" style={{ fontSize: 14, color: '#555', lineHeight: 1.5 }}>
            Deleting <strong>{getCaseDisplayTitle(casePendingDelete) ?? casePendingDelete.title}</strong>{' '}
            permanently removes its metadata, cached assets, and review progress. This action cannot be undone.
          </div>
          {deleteError && <ModalStatusMessage $tone="error">{deleteError}</ModalStatusMessage>}
          <ModalActions>
            <ModalButton type="button" onClick={handleCancelDeleteCase} disabled={isDeletingCase}>
              Cancel
            </ModalButton>
            <ModalButton
              type="button"
              $variant="danger"
              onClick={handleConfirmDeleteCase}
              disabled={isDeletingCase}
            >
              <Trash2 size={16} /> {isDeletingCase ? 'Deleting…' : 'Delete case'}
            </ModalButton>
          </ModalActions>
        </ModalPanel>
      )}
    </ModalOverlay>
  );

  const duplicatesModal = (
    <DuplicatesModal
      isOpen={duplicatesModalState !== null}
      onClose={() => setDuplicatesModalState(null)}
      datasetPath={duplicatesModalState?.datasetPath ?? ''}
      documentId={duplicatesModalState?.documentId}
      onNavigateToDocument={(docId, pageNumber) => {
        handleDocumentOpen(docId);
        setDuplicatesModalState(null);
      }}
      onRemovePage={async (docId, pageId) => {
        if (!duplicatesModalState?.datasetPath) return;
        await apiService.performBulkAction(duplicatesModalState.datasetPath, docId, {
          action: 'remove_pages',
          pageIds: [pageId],
        });
        setDetailVersion(v => v + 1);
      }}
    />
  );

  const filevinePullModal = filevinePullState ? (
    <FilevinePullModal
      onClose={() => setFilevinePullState(null)}
      onList={handleListFilevineDocuments}
      onListFolders={handleListFilevineFolders}
      onConfirm={handleFilevinePull}
      projectLabel={summary?.filevineProjectName ?? (
        typeof summary?.filevineProjectId === 'number'
          ? `project ${summary.filevineProjectId}`
          : null
      )}
    />
  ) : null;

  const filevinePushModal = filevinePushState ? (
    <FilevinePushModal
      onClose={() => setFilevinePushState(null)}
      onConfirm={handleFilevinePush}
      selectedCount={selectedDocIds.length}
      projectLabel={summary?.filevineProjectName ?? (
        typeof summary?.filevineProjectId === 'number'
          ? `project ${summary.filevineProjectId}`
          : null
      )}
    />
  ) : null;

  const linkFilevineModal = linkFilevineOpen ? (
    <LinkFilevineProjectModal
      onClose={() => setLinkFilevineOpen(false)}
      onSearch={handleSearchFilevineProjects}
      onResolve={handleResolveFilevineProject}
      onLink={handleLinkFilevineProject}
      onOpenCase={openCaseById}
    />
  ) : null;

  if (isLoading) {
    return <LoadingScreen>{authError ?? 'Authenticating...'}</LoadingScreen>;
  }

  if (!isAuthenticated) {
    return <LoadingScreen>{authError ?? 'Redirecting to login...'}</LoadingScreen>;
  }

  if (view === 'datasets') {
    return (
      <>
        <AppShell
          statusItems={statusItemsForDatasets}
          jobsIndicator={jobsIndicatorElement}
        >
          <DatasetHub
            datasets={datasets}
            isLoading={isLoadingDatasets}
            onOpen={handleDatasetOpen}
            selectedDatasetPath={selectedDataset}
            onUploadPDF={handleOpenUploadModal}
            onLinkFilevineProject={() => setLinkFilevineOpen(true)}
            onDelete={handleRequestDeleteCase}
            deletingDatasetPath={casePendingDelete?.metadataPath ?? null}
            isDeleting={isDeletingCase}
          />
        </AppShell>
        {uploadModal}
        {reprocessModal}
        {deleteModal}
        {linkFilevineModal}
        <WelcomeModal />
      </>
    );
  }

  if (view === 'inspect') {
    const currentDocumentTitle = documentDetail?.header.documentTitle ?? undefined;
    const currentDocumentId = documentDetail?.header.documentId ?? selectedDocumentId ?? undefined;

    return (
      <>
        <InspectScreen
          caseTitle={getCaseDisplayTitle(activeDataset) ?? 'Case'}
          statusItems={statusItemsForInspect}
          jobsIndicator={jobsIndicatorElement}
          documentTitle={currentDocumentTitle}
          documentId={currentDocumentId}
          onBackToOverview={handleBackToOverview}
          onBackToCases={handleBackToDatasets}
          workspaceProps={inspectWorkspaceProps}
          error={detailError}
        />
        {uploadModal}
        {reprocessModal}
        {deleteModal}
        {duplicatesModal}
        <WelcomeModal />
      </>
    );
  }

  return (
    <>
      <OverviewScreen
        caseTitle={getCaseDisplayTitle(activeDataset) ?? 'Document Overview'}
        caseSubtitle={caseSubtitle}
        statusItems={statusItemsForOverview}
        jobsIndicator={jobsIndicatorElement}
        summary={overviewSummary}
        documents={documents}
        onDocumentOpen={handleDocumentOpen}
        onDocumentAction={(action, docId) => {
          if (action === 'export') {
            void handleExportDocument(docId);
          } else if (action === 'open') {
            handleDocumentOpen(docId);
          }
        }}
        onDocumentRename={handleDocumentRename}
        selectedDocumentId={selectedDocumentId}
        emptyStateMessage={emptyOverviewMessage}
        onBackToCases={handleBackToDatasets}
        thumbnailsByDoc={documentThumbnails}
        onRequestThumbnails={handleRequestThumbnails}
        onWarningClick={handleWarningClick}
        tagPool={tagPool}
        onTagsChange={handleDocumentTagsChange}
        onCreateTag={handleCreateTag}
        onDocumentDateChange={handleDocumentDateChange}
        paginationModel={documentsPaginationModel}
        onPaginationModelChange={handleDocumentsPageChange}
        rowCount={documentsTotal}
        isLoadingDocuments={isLoadingDocuments}
        searchQuery={searchQuery}
        onSearchQueryChange={handleSearchChange}
        sortOption={sortOption}
        onSortOptionChange={handleSortChange}
        activeStatus={activeStatus}
        onStatusFilterChange={handleStatusFilterChange}
        activeCategory={activeCategory}
        onCategoryFilterChange={handleCategoryFilterChange}
        categoryTree={categoryTree}
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
        duplicateFilterDocId={duplicateFilterDocId}
        onDuplicateFilterChange={setDuplicateFilterDocId}
        selectedDocIds={selectedDocIds}
        onToggleSelection={handleToggleSelection}
        onToggleSelectAllOnPage={handleToggleSelectAllOnPage}
        onClearSelection={handleClearSelection}
        onMergeSelected={handleOpenMergeModal}
        isMergingDocs={isMergingDocs}
      />
      {uploadModal}
      {reprocessModal}
      {deleteModal}
      {duplicatesModal}
      {filevinePullModal}
      {filevinePushModal}
      <MergeDocumentsModal
        visible={mergeModalOpen && mergeSurvivorDocId != null && selectedDocs.length >= 2}
        selected={selectedDocs}
        onConfirm={(title) => { void handleConfirmMerge(title); }}
        onCancel={handleCloseMergeModal}
        isSubmitting={isMergingDocs}
      />
      <WelcomeModal />
    </>
  );
};

export default App;
